import type { ReactNode } from 'react';
import { styled } from '@classify/react-core-components/styles';
import { Link } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';

import { ROUTES } from '@openclassrooms/routing';
import Typography from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';
import Button from '@classify/react-core-components/Button';
import { translate } from '@openclassrooms/translations';

import UpvIcon from './UvpIcon';

const Title = styled('h3')(({ theme: { palette, spacing } }) => ({
  borderBottom: `1px solid ${palette.divider}`,
  paddingBottom: spacing(1),
}));

const ListItem = styled('li')(({ theme: { typography, breakpoints, spacing } }) => ({
  fontWeight: typography.fontWeightMedium,
  marginBottom: spacing(1),
  [breakpoints.up('sm')]: {
    overflow: 'hidden',
    marginBottom: 0,
  },
}));

const List = styled('ul')(({ theme: { spacing, breakpoints } }) => ({
  width: '100%',
  margin: spacing(2, 'auto'),

  [breakpoints.up('sm')]: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, auto)',
    rowGap: spacing(2),
    columnGap: spacing(3),
    padding: 0,
    listStyle: 'none',
  },

  [breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(4, auto)',
  },
}));

const ItemIcon = styled('span')(({ theme: { breakpoints, spacing } }) => ({
  display: 'none',
  [breakpoints.up('sm')]: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginRight: spacing(1),
  },
}));

export interface Props {
  children?: ReactNode;
}

const PathBanner = ({ children }: Props) => (
  <>
    <Typography variant="h3" component={Title}>
      {translate('course_home.path_promo.title')}
    </Typography>

    <Typography variant="body" component="p" sx={{ mt: 2 }}>
      {translate('course_home.path_promo.text')}
    </Typography>

    <List>
      <Typography variant="body" component={ListItem}>
        <ItemIcon>
          <UpvIcon icon={PaymentsIcon} />
        </ItemIcon>
        {translate('free_user.offer_diploma.intro.uvp1')}
      </Typography>
      <Typography variant="body" component={ListItem}>
        <ItemIcon>
          <UpvIcon icon={DateRangeIcon} />
        </ItemIcon>
        {translate('free_user.offer_diploma.intro.uvp2')}
      </Typography>
      <Typography variant="body" component={ListItem}>
        <ItemIcon>
          <UpvIcon icon={AssignmentIcon} />
        </ItemIcon>
        {translate('free_user.offer_diploma.intro.uvp3')}
      </Typography>
      <Typography variant="body" component={ListItem}>
        <ItemIcon>
          <UpvIcon icon={GroupIcon} />
        </ItemIcon>
        {translate('free_user.offer_diploma.intro.uvp4')}
      </Typography>
    </List>

    <Box sx={{ marginTop: 2, marginBottom: 4 }}>
      <Button
        variant="secondary"
        data-event="Navigation|Redirection::Internal|Path suggestion Apply"
        rel="nofollow"
        component={Link}
        to={`${ROUTES.APPLICATION.WELCOME}?appsource=generic`}
      >
        {translate('global.apply.cta')}
      </Button>
    </Box>

    {children}
  </>
);
export default PathBanner;
